import {
  createContext,
  useMemo,
  useContext,
  type ReactNode,
} from "react";
import { en, type StringKey } from "./en";

// ---------------------------------------------------------------------------
// Core translation functions
// ---------------------------------------------------------------------------

/**
 * Translate a key to its English string.
 * If the key is missing (e.g. a typo in a caller) returns the key itself
 * so the UI degrades gracefully rather than crashing.
 */
export function t(key: StringKey): string {
  const val: string | undefined = (en as Record<string, string>)[key as string];
  if (val === undefined) {
    // Missing key — return the key string so the caller can see the problem
    return key as string;
  }
  return val;
}

/**
 * Translate a key and interpolate {var} placeholders.
 *
 * @example tVars("common.nItems", { n: 5 }) → "5 items"
 */
export function tVars(
  key: StringKey,
  vars: Record<string, string | number>
): string {
  let result = t(key);
  for (const [k, v] of Object.entries(vars)) {
    result = result.replaceAll(`{${k}}`, String(v));
  }
  return result;
}

// ---------------------------------------------------------------------------
// Language context (en-only now; prepared for Hebrew)
// ---------------------------------------------------------------------------

interface LangCtx {
  lang: "en";
  dir: "ltr" | "rtl";
}

const LangContext = createContext<LangCtx>({ lang: "en", dir: "ltr" });

/** Wrap the app to provide language + direction. */
export function LangProvider({ children }: { children: ReactNode }) {
  // en-only for now; future: read from user preferences.
  // useMemo so the context value is referentially stable across renders —
  // prevents all useLang consumers from re-rendering on every provider-ancestor render.
  const value = useMemo<LangCtx>(() => ({ lang: "en", dir: "ltr" }), []);
  return (
    <LangContext.Provider value={value}>{children}</LangContext.Provider>
  );
}

/** Access the current language context. */
export function useLang(): LangCtx {
  return useContext(LangContext);
}
